import { ExternalLink } from 'lucide-react';
import type { ComponentType } from 'react';
import { JsonBlock } from '@/components/ui/json-block';
import { ExplainableFuturesAnswers } from '@/features/user-console/tabs/questionnaires/explainable-futures-answers';
import { canExplainFuturesReviewAlert } from '@/features/user-console/tabs/questionnaires/futures-assessment-explanation';
import type { ComplianceAlert } from '@/services/api/compliance-alerts';
import { isJsonObject, parseJsonIfString } from '@/utils/json';

interface AlertFieldValueProps {
  alert: ComplianceAlert;
  field: string;
  value: unknown;
}

const RawValue = ({ field, value }: AlertFieldValueProps) => {
  if (field === 'pid' && typeof value === 'string' && value) {
    return (
      <a className="inline-flex items-center gap-1 underline underline-offset-2" href={`/operations/users/${value}`} rel="noreferrer" target="_blank">
        {value}
        <ExternalLink className="size-3.5" />
      </a>
    );
  }
  if (isJsonObject(value)) {
    return <JsonBlock value={parseJsonIfString(value)} />;
  }
  return <span className="break-all">{String(value)}</span>;
};
RawValue.displayName = 'RawValue';

const FuturesReviewAnswers = (props: AlertFieldValueProps) => {
  const { alert, value } = props;

  return canExplainFuturesReviewAlert(alert) ? (
    <ExplainableFuturesAnswers answers={value} date={alert.date} pid={alert.pid} title="Futures Appropriateness" />
  ) : (
    <RawValue {...props} />
  );
};
FuturesReviewAnswers.displayName = 'FuturesReviewAnswers';

const FIELD_RENDERERS_BY_EVENT_TYPE: Partial<Record<string, Partial<Record<string, ComponentType<AlertFieldValueProps>>>>> = {
  futures_appropriateness_manual_review: { answers: FuturesReviewAnswers },
};

export const AlertFieldValue = (props: AlertFieldValueProps) => {
  const FieldRenderer = FIELD_RENDERERS_BY_EVENT_TYPE[props.alert.event_type]?.[props.field] ?? RawValue;

  return <FieldRenderer {...props} />;
};
AlertFieldValue.displayName = 'AlertFieldValue';
